import { mode, StyleFunctionProps } from '@chakra-ui/theme-tools';

import { convertHexToRGBAString } from '_helpers/convertHexToRGB';
import COLORS from 'theme/colors';

type ElevationSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export enum ElevationSizes {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl'
}

const rgbaByOpacityPercentage = {
  2: convertHexToRGBAString(COLORS.black.base, 0.02),
  4: convertHexToRGBAString(COLORS.black.base, 0.04),
  8: convertHexToRGBAString(COLORS.black.base, 0.08),
  16: convertHexToRGBAString(COLORS.black.base, 0.16),
  24: convertHexToRGBAString(COLORS.black.base, 0.24),
  32: convertHexToRGBAString(COLORS.black.base, 0.32)
};

const elevationMap = {
  [ElevationSizes.xs]: {
    _light: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[16]}, 0px 1px 1px ${rgbaByOpacityPercentage[4]}`
    },
    _dark: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[24]}, 0px 1px 0px ${rgbaByOpacityPercentage[8]}`
    }
  },
  [ElevationSizes.sm]: {
    _light: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[8]}, 0px 2px 2px ${rgbaByOpacityPercentage[4]}, 0px 2px 4px -2px ${rgbaByOpacityPercentage[8]}`
    },
    _dark: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[24]}, 0px 2px 2px ${rgbaByOpacityPercentage[16]}, 0px 2px 4px -2px ${rgbaByOpacityPercentage[24]}`
    }
  },
  [ElevationSizes.md]: {
    _light: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[8]}, 0px 2px 2px ${rgbaByOpacityPercentage[4]}, 0px 4px 8px -2px ${rgbaByOpacityPercentage[8]}`
    },
    _dark: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[24]}, 0px 2px 2px ${rgbaByOpacityPercentage[16]}, 0px 4px 8px -2px ${rgbaByOpacityPercentage[32]}`
    }
  },
  [ElevationSizes.lg]: {
    _light: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[8]}, 0px 4px 8px ${rgbaByOpacityPercentage[4]}, 0px 8px 16px -2px ${rgbaByOpacityPercentage[4]}, 0px 16px 32px -4px ${rgbaByOpacityPercentage[4]}`
    },
    _dark: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[24]}, 0px 4px 8px ${rgbaByOpacityPercentage[16]}, 0px 8px 16px -2px ${rgbaByOpacityPercentage[16]}, 0px 16px 32px -4px ${rgbaByOpacityPercentage[32]}`
    }
  },
  [ElevationSizes.xl]: {
    _light: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[8]}, 0px 8px 16px ${rgbaByOpacityPercentage[2]}, 0px 16px 32px -4px ${rgbaByOpacityPercentage[4]}, 0px 32px 64px -8px ${rgbaByOpacityPercentage[4]}`
    },
    _dark: {
      boxShadow: `0px 0px 0px 0.5px ${rgbaByOpacityPercentage[24]}, 0px 8px 16px ${rgbaByOpacityPercentage[8]}, 0px 16px 32px -4px ${rgbaByOpacityPercentage[16]}, 0px 32px 64px -8px ${rgbaByOpacityPercentage[32]}`
    }
  }
};

export default function getElevation(props: unknown, size: ElevationSize): string {
  const {
    _light: { boxShadow: lightModeBoxShadow },
    _dark: { boxShadow: darkModeBoxShadow }
  } = elevationMap[size];

  return mode(lightModeBoxShadow, darkModeBoxShadow)(props as StyleFunctionProps);
}
